import { Card, Skeleton, Stack, Typography } from "@mui/material";
import DonutChart, { IPieData } from "../../../common/PieChart";
import { useEffect, useState } from "react";
import { useNotificationContext } from "../../../../context/notificationContext";
import { useProtectedAxiosInstance } from "../../../../hooks/axiosInstance";
import { useNavigate } from "react-router-dom";
import errorResolver from "../../../../utils/error-resolver";
import { format } from "date-fns";
import FilterDonut from "./FilterDonut";
import ReloadButton from "../../../common/reload-button/ReloadButton";
import { filterOptions } from "./filterOptions";
import {
  BroadcastingDetailsRequest,
  BroadcastingReportListColumnFilterable,
  VoiceBroadCastReportSchema,
} from "../../../../services/Endpoints/broadcast";
import { FilterOperatorEnum } from "../../../../services/Schemas/ListQueryString";
import MuiLinearProgress from "../../common/MuiLinearProgress";

interface IBroadCastingMedSecProps {
  type?: string;
  id?: number | string;
  loading?: boolean;
  initialReports?: VoiceBroadCastReportSchema[] | null;
}

const COLORS = [
  "#E74C3C", // Vibrant Red
  "#2980B9", // Bright Blue
  "#27AE60", // Emerald Green
  "#8E44AD", // Purple
  "#F39C12", // Orange
  "#16A085", // Turquoise
  "#D35400", // Pumpkin
  "#2C3E50", // Dark Slate Blue
  "#C0392B", // Dark Red
  "#7F8C8D", // Gray
];

const handleCallInput = (reports: VoiceBroadCastReportSchema[]) => {
  const result = {};

  reports.forEach((report) => {
    if (!report.call_input || report.total <= 0) {
      return;
    }

    // @ts-ignore
    if (!result[report.call_input]) {
      // @ts-ignore
      result[report.call_input as unknown] = {
        value: 0,
        name: `عدد ${report.call_input}`,
        color: COLORS[report.call_input as any] ?? "#ccc",
        valueDisplay: ` مخاطب`,
      };
    }
    // @ts-ignore
    result[report.call_input as unknown].value += report.total;
  });

  return Object.values(result);
};

export default function BroadcastNumReactionReport({
  id,
  type,
  loading,
  initialReports = null,
}: Readonly<IBroadCastingMedSecProps>) {
  const [rawFilter, setRawFilter] = useState(filterOptions[0].value.start);

  const [reports, setReports] = useState<VoiceBroadCastReportSchema[]>([]);
  const [timeFilter, setTimeFilter] = useState<string[] | null>(null);
  const [waiting, setWaiting] = useState(false);

  const { setNotification } = useNotificationContext();
  const protectedInstance = useProtectedAxiosInstance();
  const navigate = useNavigate();
  const [reload, setReload] = useState(false);

  const handleReload = () => {
    setReload(!reload);
  };

  const getData = async () => {
    if (id) {
      try {
        setWaiting(true);
        const broadcastingDetailsRequest = new BroadcastingDetailsRequest(
          protectedInstance
        );
        const { data: _data } = await broadcastingDetailsRequest.report(
          {
            broadcastingNumber: id.toString(),
          },
          {
            filters: timeFilter
              ? [
                  {
                    column: BroadcastingReportListColumnFilterable.UPDATED_AT,
                    operator: FilterOperatorEnum.BETWEEN,
                    value: timeFilter,
                  },
                ]
              : [],
          }
        );
        setReports(_data.number_reports);
        setWaiting(false);
      } catch (error: any) {
        errorResolver({ error, setNotification });
        setWaiting(false);
        navigate("/404");
        console.error(error.message);
      }
    }
  };

  useEffect(() => {
    initialReports && getData();
  }, [timeFilter, reload]);

  useEffect(() => {
    initialReports && setReports(initialReports);
  }, [initialReports]);

  const handleTimeFilter = (rawFilter: { start: string; end: string }) => {
    if (!waiting) {
      setRawFilter(rawFilter.start);
      setTimeFilter([
        format(rawFilter.start, "yyyy-MM-dd HH:mm:ss"),
        format(rawFilter.end, "yyyy-MM-dd HH:mm:ss"),
      ]);
    }
  };

  return (
    <>
      {type != "simple" && (
        <Card
          bgcolor="#fff"
          height="inherit"
          mx="0 !important"
          component={Stack}
          gap="10px"
          sx={{
            borderRadius: "8px",
            py: "14px",
            px: "16px",
            borderColor: (theme) => theme.palette.others.borderColor,
            borderWidth: "1px",
            borderStyle: "solid",
            boxShadow: "none",
            position: "relative",
          }}
        >
          {waiting && <MuiLinearProgress />}
          <Stack direction="row" justifyContent="space-between">
            <Stack direction="row" gap="10px" justifyContent="start">
              {!loading ? (
                <FilterDonut
                  setOption={handleTimeFilter}
                  optionsList={filterOptions}
                  active={
                    filterOptions.filter(
                      (itm) => itm.value.start == rawFilter
                    )[0].key
                  }
                />
              ) : (
                Array(5)
                  .fill("")
                  .map((_, i) => (
                    <Skeleton key={i} width="100px" height="40px" />
                  ))
              )}
            </Stack>

            {!loading ? (
              <ReloadButton reloadAction={handleReload} />
            ) : (
              <Skeleton variant="rectangular" width="40px" height="40px" />
            )}
          </Stack>

          {!loading ? (
            <>
              <Typography
                variant="h6MediumBold"
                color={(thm) => thm.palette.dominant.main}
              >
                گزارش نظر سنجی
              </Typography>
              <Typography
                variant="captionMediumBold"
                color={(thm) => thm.palette.dominant.main}
              >
                در این بخش، بازخوردهای مخاطبان به پیام صوتی شما نمایش داده
                می‌شود.
              </Typography>
            </>
          ) : (
            <>
              <Skeleton variant="rectangular" width="200px" height="20px" />
              <Skeleton variant="rectangular" width="500px" height="20px" />
            </>
          )}
          <Stack justifyContent="center" alignItems="center">
            {reports ? (
              <DonutChart
                data={handleCallInput(reports) as IPieData[]}
                pieWrapSx={{
                  width: "100%",
                  flexDirection: "row-reverse",
                  alignItems: { xs: "start", lg: "center" },
                  justifyContent: { xs: "start", lg: "space-between" },
                }}
                legendSx={{
                  flexDirection: "column",
                }}
                loading={(waiting && reports.length == 0) || loading}
              />
            ) : (
              <Typography
                sx={{
                  textAlign: "center",
                  width: "100%",
                  py: "30px",
                }}
              >
                {!waiting && <>دیتایی جهت نمایش وجود ندارد.</>}
              </Typography>
            )}
          </Stack>
        </Card>
      )}
    </>
  );
}
